import React, { useCallback, useEffect, useRef } from 'react';
import classNames from 'classnames';

import { CloseMobile } from '@/icons/index';

import styles from './Modal.module.scss';

type Props = {
  active: boolean;
  setActive: (isActive: boolean) => void;
  children?: React.ReactNode;
  className?: string;
  classNameInner?: string;
  showCloseIcon?: boolean;
  id?: string;
};

const Modal = ({
  active = false,
  setActive = () => null,
  children,
  className,
  classNameInner,
  showCloseIcon = true,
  ...restProps
}: Props) => {
  const modalRef = useRef(null);

  const closeModal = useCallback(
    (event) => {
      if (event.key === 'Escape') {
        setActive(false);
      }
    },
    [setActive],
  );

  const handleClickOutside = useCallback(
    (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setActive(false);
      }
    },
    [setActive],
  );

  useEffect(() => {
    document.addEventListener('keydown', closeModal);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('keydown', closeModal);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [closeModal, handleClickOutside]);

  useEffect(() => {
    if (active) {
      document.documentElement.classList.add('overflow');
    } else {
      document.documentElement.classList.remove('overflow');
    }

    return () => {
      document.documentElement.classList.remove('overflow');
    };
  }, [active]);

  const handleClose = useCallback(() => {
    setActive(false);
  }, [setActive]);

  return (
    <div
      className={classNames([
        styles.wrap,
        { [styles.open]: active },
        className,
      ])}
      itemScope
      itemType='https://schema.org/Property'
      {...restProps}
    >
      <div
        ref={modalRef}
        className={classNames([styles.inner, classNameInner])}
      >
        <div className={styles.content}>
          {showCloseIcon && (
            <div
              data-testid={`modal-close${
                restProps['data-testid'] ? `-${restProps['data-testid']}` : ''
              }`}
              className={styles.close}
              onClick={handleClose}
            >
              <CloseMobile />
            </div>
          )}
          {children}
        </div>
      </div>
    </div>
  );
};

export default Modal;
